import { Form as AntdForm } from '@ant-design/compatible';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { BANNER_WIDGET_ITEM_LINK_TYPE } from '@amondz/constants';
import dayjs from 'dayjs';

import Form from '../../../../components/form/Form';
import FormWithTab from '../../../../components/form/Form/FormWithTab';
import FormListTab from '../../../../components/input/FormListTab/FormListTab';
import Panel from '../../../../components/Panel';
import Notice from '../../../../components/Notice';
import Button from '../../../../components/Button';
import { SlideTabContent } from './SlideTabContent';
import { SlideTabLabel } from './SlideTabLabel';
import { CategoryBannerView } from './CategoryBannerView';
import { useCategoryBannerForm } from './hooks/useCategoryBannerForm';
import { DATE_FORMAT, MAX_SLIDE_COUNT } from './constants';
import { CategoryBannerSlideFormItem } from './types';
import { EmptyStateStyle, ButtonListStyle } from './CategoryBannerTab.style';

type CategoryBannerTabProps = {
  form: WrappedFormUtils;
};

// FormListTab의 `initialTabItemValue`로 주입되며, id는 FormListTab이 추가 시점에 자동 부여
const getDefaultSlideValue = (): Omit<CategoryBannerSlideFormItem, 'id'> => ({
  mobileImage: null,
  pcImage: null,
  isNonLink: false,
  linkType: BANNER_WIDGET_ITEM_LINK_TYPE.EVENT,
  link: null,
  linkUrl: '',
  exposurePeriod: {
    startDate: dayjs().format(DATE_FORMAT),
    endDate: null,
  },
});

const CategoryBannerTabInner = ({ form }: CategoryBannerTabProps) => {
  const {
    data,
    isLoading,
    isError,
    isEditable,
    handleStartEdit,
    currentTab,
    setCurrentTab,
    isSaving,
    isDeleting,
    handleFinish,
    handleCancel,
    getInnerRules,
    formRules,
  } = useCategoryBannerForm({ form });

  if (isLoading) return <div>로딩 중...</div>;
  if (isError || !data) return <div>배너 정보를 불러오지 못했습니다.</div>;

  const hasBanner = data.length > 0;
  // 저장(upsert)과 빈 저장(delete)은 동일 배너 리소스/캐시를 덮어쓰므로 둘 중 하나만 진행되도록 묶는다.
  const isSubmitting = isSaving || isDeleting;

  if (!isEditable) {
    if (!hasBanner) {
      return (
        <EmptyStateStyle>
          <p>등록된 배너가 없습니다.</p>
          <Button isRadius color="primary" text="새로 등록" onClick={handleStartEdit} />
        </EmptyStateStyle>
      );
    }

    return (
      <div>
        <CategoryBannerView slides={data} />
        <ButtonListStyle>
          <Button isRadius color="primary" text="수정" onClick={handleStartEdit} />
        </ButtonListStyle>
      </div>
    );
  }

  return (
    <FormWithTab
      name="slides"
      form={form}
      onFinish={handleFinish}
      scrollToFirstError
    >
      <Panel title="배너 슬라이드 관리">
        <Panel.Section>
          <Form.Item name="slides" rules={formRules.slides.rules}>
            <FormListTab
              max={MAX_SLIDE_COUNT}
              notice={
                <Notice>* 드래그 앤 드랍으로 슬라이드 순서를 변경할 수 있습니다.</Notice>
              }
              initialTabItemValue={getDefaultSlideValue()}
              onChangeActiveKey={setCurrentTab}
              activeKey={currentTab}
              items={(value: CategoryBannerSlideFormItem[]) =>
                value.map((item, idx) => ({
                  id: item.id,
                  // FormListTab의 TabItem.title 타입은 string이지만 antd Tabs가 ReactNode를 허용하므로
                  // 썸네일 포함 커스텀 라벨을 렌더하기 위해 캐스팅한다. (FormListTab 타입 확장은 별도 PR로)
                  title: (<SlideTabLabel index={idx} thumbnail={item.mobileImage?.url} />) as unknown as string,
                  content: (tabForm: WrappedFormUtils) => (
                    <SlideTabContent
                      item={item}
                      tabForm={tabForm}
                      innerRules={getInnerRules(item)}
                    />
                  ),
                }))
              }
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <ButtonListStyle>
        <Button
          isRadius
          color="primary"
          text="저장"
          disabled={isSubmitting}
          type="submit"
        />
        <Button isRadius text="취소" type="button" onClick={handleCancel} disabled={isSubmitting} />
      </ButtonListStyle>
    </FormWithTab>
  );
};

export const CategoryBannerTab = AntdForm.create<CategoryBannerTabProps>()(CategoryBannerTabInner);
